import styled, { css } from 'styled-components';
import {
  TEXT_FIELD_APPEARANCE_NAKED,
  TEXT_FIELD_APPEARANCE_OUTLINED,
  TEXT_FIELD_APPEARANCE_PILL,
  TEXT_FIELD_SIZE_TOKENS,
  type TextFieldAppearance,
  type TextFieldSize,
} from './TextField.constants';

interface WrapperProps {
  $appearance: TextFieldAppearance;
  $size: TextFieldSize;
  $invalid: boolean;
  $disabled: boolean;
  $stretch: boolean;
}

type AppearancePalette = {
  errorBorder: string;
  restingBorder: string;
  focusBorder: string;
  outline: string;
};

const palette = (colors: Record<string, string | undefined>): AppearancePalette => ({
  errorBorder: colors.error ?? '#FFB4AB',
  restingBorder: colors.outlineVariant ?? '#464555',
  focusBorder: colors.primary ?? '#C1C1FF',
  outline: colors.outline ?? '#908FA0',
});

const nakedStyles = (invalid: boolean, p: AppearancePalette) => css`
  background: transparent;
  box-shadow: ${invalid ? `inset 0 -1px 0 ${p.errorBorder}` : 'none'};
  &:focus-within {
    box-shadow: inset 0 -1px 0 ${invalid ? p.errorBorder : p.focusBorder};
  }
`;

const outlinedStyles = (
  invalid: boolean,
  p: AppearancePalette,
  colors: Record<string, string | undefined>,
) => {
  const surface = colors.surfaceContainerLow ?? '#1B1B23';
  const surfaceFocus = colors.surfaceContainer ?? '#1F1F27';
  return css`
    background: ${surface};
    box-shadow: inset 0 0 0 1px ${invalid ? p.errorBorder : p.restingBorder};
    &:hover {
      box-shadow: inset 0 0 0 1px ${invalid ? p.errorBorder : p.outline};
    }
    &:focus-within {
      background: ${surfaceFocus};
      box-shadow: inset 0 0 0 2px ${invalid ? p.errorBorder : p.focusBorder};
    }
  `;
};

// Pill fields deepen the surface tone on focus instead of growing a
// border. The invalid state paints an outer error halo (not inset) so
// the pill chrome stays unbroken.
const pillStyles = (
  invalid: boolean,
  p: AppearancePalette,
  colors: Record<string, string | undefined>,
) => {
  const surface = colors.surfaceContainer ?? '#1F1E27';
  const surfaceFocus = colors.surfaceContainerHigh ?? '#1F2030';
  const errorHalo = `0 0 0 2px color-mix(in oklch, ${p.errorBorder} 60%, transparent)`;
  return css`
    background: ${surface};
    box-shadow: ${invalid ? errorHalo : 'none'};
    &:focus-within {
      background: ${surfaceFocus};
      box-shadow: ${invalid ? errorHalo : 'none'};
    }
  `;
};

const appearanceStyles = (
  appearance: TextFieldAppearance,
  invalid: boolean,
) => css`
  ${({ theme: { colors = {} } }) => {
    const p = palette(colors);
    if (appearance === TEXT_FIELD_APPEARANCE_NAKED) return nakedStyles(invalid, p);
    if (appearance === TEXT_FIELD_APPEARANCE_OUTLINED)
      return outlinedStyles(invalid, p, colors);
    if (appearance === TEXT_FIELD_APPEARANCE_PILL)
      return pillStyles(invalid, p, colors);
    return null;
  }}
`;

export const Wrapper = styled.div<WrapperProps>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  transition:
    background ${({ theme: { motion } }) => motion?.duration?.fast ?? '120ms'}
      ${({ theme: { motion } }) => motion?.easing?.standard ?? 'cubic-bezier(0.2,0,0,1)'},
    box-shadow ${({ theme: { motion } }) => motion?.duration?.fast ?? '120ms'}
      ${({ theme: { motion } }) => motion?.easing?.standard ?? 'cubic-bezier(0.2,0,0,1)'};

  ${({ $size, $appearance }) => {
    const tokens = TEXT_FIELD_SIZE_TOKENS[$size];
    // Naked appearance has no padding so the input lines up flush with the
    // surrounding text it visually replaces (e.g. in an inline rename).
    // Pill fully rounds to a stadium shape; outlined uses the standard 8px.
    let radius = '8px';
    if ($appearance === TEXT_FIELD_APPEARANCE_NAKED) radius = '0';
    else if ($appearance === TEXT_FIELD_APPEARANCE_PILL) radius = '999px';
    return css`
      height: ${tokens.height};
      padding: 0
        ${$appearance === TEXT_FIELD_APPEARANCE_NAKED ? '0' : tokens.padX};
      border-radius: ${radius};
    `;
  }}

  ${({ $stretch }) =>
    $stretch
      ? css`
          width: 100%;
          flex: 1 1 auto;
          min-width: 0;
        `
      : css`
          min-width: 140px;
        `}

  ${({ $appearance, $invalid }) => appearanceStyles($appearance, $invalid)}

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    `}
`;

export const LeadingSlot = styled.span<{ $size: TextFieldSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: 0.7;
  ${({ $size }) => {
    const tokens = TEXT_FIELD_SIZE_TOKENS[$size];
    return css`
      width: ${tokens.iconSize};
      height: ${tokens.iconSize};
      & > svg,
      & > .material-symbols-rounded {
        font-size: ${tokens.iconSize};
        width: ${tokens.iconSize};
        height: ${tokens.iconSize};
      }
    `;
  }}
`;

export const TrailingSlot = styled(LeadingSlot)`
  /* Same dimensions as LeadingSlot — separate component so consumers can
     target either slot for spacing/visibility tweaks via the data-testid. */
`;

export const Input = styled.input<{ $size: TextFieldSize }>`
  flex: 1 1 auto;
  min-width: 0;
  background: transparent;
  border: 0;
  outline: none;
  padding: 0;
  font: inherit;
  appearance: none;
  -webkit-appearance: none;

  ${({ $size }) => css`
    font-size: ${TEXT_FIELD_SIZE_TOKENS[$size].fontSize};
  `}

  ${({ theme: { colors = {} } }) => css`
    color: ${colors.onSurface ?? '#E4E1ED'};
    &::placeholder {
      color: ${colors.onSurfaceVariant ?? '#C7C4D7'};
      opacity: 0.7;
    }
  `}

  &:disabled {
    cursor: not-allowed;
  }
`;
